import angular from 'angular';
import { Guid } from '@Shared.Angular/@types/guid';
import { FormFieldType } from './flowingly.constants';
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';
import { colorPalette } from '@Shared.Angular/flowingly.services/data/colorPalette';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import IFormThemeOverview from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeOverview';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IFormThemeDetail, {
  FormThemeFontColorType,
  FormThemeLogoAlignment
} from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeDetail';
import { Services } from '@Shared.Angular/@types/services';

export class ThemeService {
  static readonly DEFAULT_FONT_COLOR = '#35384D';
  static readonly DEFAULT_FORM_BG_COLOR = '#EEEEEE';
  static readonly DEFAULT_PRIMARY_BUTTON_COLOR = '#4CAF50';
  static readonly DEFAULT_CONTRAST_COLOR = '#FFFFFF';

  constructor(
    private formThemesApiService: SharedAngular.FormThemesApiService
  ) {}

  getDefaultFormBackgroundColor(): string {
    return ThemeService.DEFAULT_FORM_BG_COLOR;
  }

  applyThemeToField(fields: IFormField[], theme: IFormThemeDetail) {
    if (!fields) return;

    fields.forEach((field) => {
      switch (field.type) {
        case FormFieldType.INSTRUCTION:
          field.value = this.replaceNonThemeColors(field.value, theme);
          break;

        case FormFieldType.IMAGE:
          this.applyThemeToLogoField(field, theme);
          break;
      }
    });
  }

  getEffectiveColors(theme: IFormThemeDetail) {
    return theme?.fontColorType === FormThemeFontColorType.Custom
      ? theme.fontColors
      : colorPalette;
  }

  replaceNonThemeColors(fieldValue: string, theme: IFormThemeDetail): string {
    if (!fieldValue) return fieldValue;

    const fontColors = this.getEffectiveColors(theme);
    const defaultColor = ThemeService.DEFAULT_FONT_COLOR;

    // COLOR_REGEX matches CSS color properties with 6 digit hex values, excluding those prefixed by word characters or hyphens.
    const COLOR_REGEX = /(?<![-\w])color\s*:\s*(#(?:[0-9a-fA-F]{6}))\b/gi;
    const STYLE_ATTR_REGEX = /style="([^"]*)"/gi;

    return fieldValue.replace(STYLE_ATTR_REGEX, (styleMatch, styleContent) => {
      const updatedStyle = styleContent.replace(COLOR_REGEX, (match, hex) => {
        const hexValue = hex.toUpperCase();
        const finalColor = fontColors
          .map((color) => color.toUpperCase())
          .includes(hexValue)
          ? hexValue
          : defaultColor;
        return `color: ${finalColor}`;
      });
      return `style="${updatedStyle}"`;
    });
  }

  findLogoField(fields: IFormField[]) {
    if (!fields) return null;

    return fields.find(
      (field) =>
        field.type === FormFieldType.IMAGE && field.displayName === 'Logo'
    );
  }

  findLogoFieldIndex(fields: IFormField[]) {
    if (!fields) return -1;

    return fields.findIndex(
      (field) =>
        field.type === FormFieldType.IMAGE && field.displayName === 'Logo'
    );
  }

  applyThemeToLogoField(logoField: IFormField, theme: IFormThemeDetail) {
    if (!logoField || !theme) return logoField;

    logoField.readOnly = true;
    logoField.actionButtons = {
      ...(logoField.actionButtons || {}),
      hideDelete: true,
      hideSettings: true
    };

    if (theme.logoFileId) {
      logoField.value = theme.logoFileId;
      logoField.alignment =
        FormThemeLogoAlignment[theme.logoAlignment]?.toLowerCase() ||
        logoField.alignment;
      logoField.locked = true;
      logoField.disabled = false;
      logoField.hideInDesigner = false;
    } else {
      logoField.hideInDesigner = true;
    }

    return logoField;
  }

  resetLogoFieldToEditable(logoField: IFormField) {
    if (!logoField) return logoField;

    logoField.readOnly = false;
    logoField.locked = false;
    logoField.disabled = true;
    logoField.actionButtons = {
      ...(logoField.actionButtons || {}),
      hideDelete: true,
      hideSettings: false
    };

    return logoField;
  }

  isTaskTypeThemable(taskType: ActivityTaskType): boolean {
    return (
      taskType === ActivityTaskType.PUBLIC_FORM ||
      taskType === ActivityTaskType.EXTERNAL_USER_TASK
    );
  }

  loadPublishedThemes(): Promise<IFormThemeOverview[]> {
    return this.formThemesApiService.getPublishedFormThemes();
  }

  loadPublishedThemeDetailsWithFallback(
    themes: IFormThemeOverview[],
    themeId?: Guid
  ): Promise<IFormThemeDetail | null> {
    const selected = this.findOrFallbackTheme(themes, themeId);
    if (!selected) return Promise.resolve(null);
    return this.formThemesApiService.getPublishedFormTheme(selected.id);
  }

  async loadPublishedThemesAndDetail(themeId?: Guid): Promise<{
    themes: IFormThemeOverview[];
    theme: IFormThemeDetail | null;
  }> {
    const themes = await this.loadPublishedThemes();
    const theme = await this.loadPublishedThemeDetailsWithFallback(
      themes,
      themeId
    );
    return { themes, theme };
  }

  findOrFallbackTheme(
    themes: IFormThemeOverview[],
    themeId: Guid | null
  ): IFormThemeOverview | null {
    if (!themes || themes.length === 0) return null;

    if (themeId) {
      const found = themes.find((t) => t.id === themeId);
      if (found) return found;
    }

    return themes.find((t) => t.isDefaultTheme);
  }

  getContrastColor(bgColor: string): string {
    if (!bgColor || typeof bgColor !== 'string') {
      return ThemeService.DEFAULT_CONTRAST_COLOR;
    }

    const hex = bgColor.replace('#', '');
    if (hex.length !== 6) {
      return ThemeService.DEFAULT_CONTRAST_COLOR;
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);

    // Perceived brightness based on human eye sensitivity
    const brightness = red * 0.299 + green * 0.587 + blue * 0.114;

    return brightness > 140
      ? ThemeService.DEFAULT_FONT_COLOR
      : ThemeService.DEFAULT_CONTRAST_COLOR;
  }
}

angular
  .module('flowingly.services')
  .factory('themeService', [
    'formThemesApiService',
    (formThemesApiService: Services.FormThemesApiService) =>
      new ThemeService(formThemesApiService)
  ]);

export type ThemeServiceType = InstanceType<typeof ThemeService>;
